import { useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toastService } from "../../../../providers";
import * as roleQueries from "../../../../hooks/queries/useRoleQueries";

export const useUserRoleManagement = ({ isSuperAdmin }) => {
  const [manageRolesModalOpen, setManageRolesModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { roleIds: [] },
    mode: "onTouched",
  });

  const {
    data: roles = [],
    isLoading: isLoadingRoles,
    isError: isrolesError,
    refetch: refetchRoles,
  } = roleQueries.useGetAllRolesQuery({ enabled: isSuperAdmin });

  const { mutate: assignRolesToUser, isPending: isAssigningRoles } =
    roleQueries.useAssignRolesToUserMutation();

  const assignableRoles = useMemo(
    () => roles.filter((role) => role.name !== "ROLE_SUPER_ADMIN"),
    [roles],
  );

  const assignableRoleIdSet = useMemo(
    () => new Set(assignableRoles.map((role) => role.id)),
    [assignableRoles],
  );

  const superAdminRoleIdSet = useMemo(
    () =>
      new Set(
        roles
          .filter((role) => role.name === "ROLE_SUPER_ADMIN")
          .map((role) => role.id),
      ),
    [roles],
  );

  const handleManageRoles = useCallback(
    (user) => {
      setSelectedUser(user);
      if (!roles || !Array.isArray(roles)) {
        toastService.warning("Roles data is not available.");
        reset({ roleIds: [] });
        setManageRolesModalOpen(true);
        return;
      }
      const roleIdsFromUser = Array.isArray(user.roles)
        ? user.roles
            .map((userRole) => {
              const foundRole = roles.find(
                (r) => r.name === userRole || r.id === userRole,
              );
              return foundRole?.id;
            })
            .filter((roleId) => roleId && assignableRoleIdSet.has(roleId))
        : [];

      reset({ roleIds: roleIdsFromUser });
      setManageRolesModalOpen(true);
    },
    [roles, reset, assignableRoleIdSet],
  );

  const onSubmit = useCallback(
    ({ roleIds }) => {
      if (!selectedUser?.id) {
        toastService.error("No user selected for role assignment.");
        return;
      }

      const sanitizedRoleIds = Array.from(
        new Set(
          (Array.isArray(roleIds) ? roleIds : []).filter(
            (roleId) =>
              roleId &&
              assignableRoleIdSet.has(roleId) &&
              !superAdminRoleIdSet.has(roleId),
          ),
        ),
      );

      assignRolesToUser(
        { userId: selectedUser.id, roleIds: sanitizedRoleIds },
        {
          onSuccess: () => {
            toastService.success("Roles updated successfully!");
            setManageRolesModalOpen(false);
            setSelectedUser(null);
          },
          onError: () => {
            toastService.error("Failed to update roles");
          },
        },
      );
    },
    [assignRolesToUser, selectedUser, assignableRoleIdSet, superAdminRoleIdSet],
  );

  const handleCloseMerchantRolesModal = useCallback(() => {
    setManageRolesModalOpen(false);
    setSelectedUser(null);
    reset({ roleIds: [] });
  }, [reset]);

  const handleRetryRoles = useCallback(() => {
    refetchRoles();
  }, [refetchRoles]);

  return {
    manageRolesModalOpen,
    selectedUser,
    control,
    handleSubmit,
    onSubmit,
    roles: assignableRoles,
    isLoadingRoles,
    isrolesError,
    isAssigningRoles,
    handleManageRoles,
    handleCloseMerchantRolesModal,
    handleRetryRoles,
  };
};

export default useUserRoleManagement;
